import type { FeatureCollection, Point } from "geojson";
import { GeoJSONSource, LngLatBounds, MapMouseEvent } from "mapbox-gl";
import { RefObject, TransitionEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MapboxConstants } from "../../../constants";
import { logError } from "../../../service/error";
import mapboxgl from "../../../service/mapbox";
import { DashboardMapColourBy, DashboardMapPin, DashboardMapPinColour, DashboardMapProps } from "./DashboardMapProps";
import { applyBrandMapColours } from "./utils/mapColours";
import { createPinImage, PIN_PIXEL_RATIO, pinImageId, resolvePinColour } from "./utils/pinImages";

const SOURCE_ID = "dashboard-map-pins";
const CLUSTER_LAYER_ID = "dashboard-map-clusters";
const CLUSTER_COUNT_LAYER_ID = "dashboard-map-cluster-counts";
const PIN_LAYER_ID = "dashboard-map-unclustered-pins";

const CLUSTER_RADIUS: mapboxgl.Expression = ["step", ["get", "point_count"], 14, 10, 18, 30, 22];

/* How long markers take to fade/scale in when they appear or the zoom changes */
const MARKER_ANIMATION_MS = 260;

/* Opacity paint properties faded in on load and on every zoom change */
const MARKER_OPACITY_PROPS: [string, string][] = [
  [PIN_LAYER_ID, "icon-opacity"],
  [CLUSTER_LAYER_ID, "circle-opacity"],
  [CLUSTER_LAYER_ID, "circle-stroke-opacity"],
  [CLUSTER_COUNT_LAYER_ID, "text-opacity"],
];

/* Fades markers in (and pops the cluster circles up from a smaller radius) whenever they
   (re)appear — initial load, cluster expansion, and zoom in/out all route through here.
   Property names are built dynamically, so go through a loosely-typed setter. */
const setPaint = (map: mapboxgl.Map, layerId: string, prop: string, value: unknown): void => {
  if (!map.getLayer(layerId)) return;
  (map.setPaintProperty as (layer: string, name: string, val: unknown) => void)(layerId, prop, value);
};

const animateMarkersIn = (map: mapboxgl.Map): void => {
  MARKER_OPACITY_PROPS.forEach(([layerId, prop]) => {
    setPaint(map, layerId, `${prop}-transition`, { duration: 0, delay: 0 });
    setPaint(map, layerId, prop, 0);
  });
  setPaint(map, CLUSTER_LAYER_ID, "circle-radius-transition", { duration: 0, delay: 0 });
  setPaint(map, CLUSTER_LAYER_ID, "circle-radius", 4);

  requestAnimationFrame(() => {
    MARKER_OPACITY_PROPS.forEach(([layerId, prop]) => {
      setPaint(map, layerId, `${prop}-transition`, { duration: MARKER_ANIMATION_MS, delay: 0 });
      setPaint(map, layerId, prop, 1);
    });
    setPaint(map, CLUSTER_LAYER_ID, "circle-radius-transition", { duration: MARKER_ANIMATION_MS, delay: 0 });
    setPaint(map, CLUSTER_LAYER_ID, "circle-radius", CLUSTER_RADIUS);
  });
};

/* Auto-assign order matches the design system's project-type tags: woodland green, peatland coral, biodiversity mint */
const PIN_COLOURS: DashboardMapPinColour[] = [
  "green",
  "coral",
  "mint-green",
  "goldenrod",
  "blue",
  "sky-blue",
  "aqua",
  "magenta",
  "violet",
  "navy",
];

interface OverlayPoint {
  x: number;
  y: number;
}

/* Slightly longer than the CSS height transition so transitionend has first say */
const COLLAPSE_SETTLE_MILLISECONDS = 500;

interface KeyEntry {
  label: string;
  colour: DashboardMapPinColour;
}

interface UseDashboardMapReturn {
  mapContainerRef: RefObject<HTMLDivElement>;
  hasError: boolean;
  filterOptions: string[];
  selectedFilters: string[];
  filterDimension: DashboardMapColourBy;
  isFilterOpen: boolean;
  filterRef: RefObject<HTMLDivElement>;
  keyRef: RefObject<HTMLDivElement>;
  cardRef: RefObject<HTMLDivElement>;
  keyEntries: KeyEntry[];
  isKeyOpen: boolean;
  selectedPin: DashboardMapPin | null;
  overlayPoint: OverlayPoint | null;
  isEmpty: boolean;
  isExpanded: boolean;
  containerHeight: number | string;
  canvasHeight: number | string;
  searchText: string;
  searchRef: RefObject<HTMLDivElement>;
  searchResults: DashboardMapPin[];
  isSearchListOpen: boolean;
  pinColourFor: (pin: DashboardMapPin) => DashboardMapPinColour;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onSelectSearchResult: (pin: DashboardMapPin) => void;
  onToggleFilterOpen: () => void;
  onToggleFilter: (value: string) => void;
  onClearFilters: () => void;
  onToggleKeyOpen: () => void;
  onToggleExpand: () => void;
  onContainerTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export const useDashboardMap = ({
  pins,
  accessToken,
  colourBy = "type",
  categoryColours,
  phaseColours,
  defaultPinColour = "navy",
  height = 466,
  expandedHeight = 640,
  isExpanded: isExpandedProp = false,
  onExpandToggle,
}: DashboardMapProps): UseDashboardMapReturn => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const keyRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  /* Tracks the pin id already nudged into view, so we nudge once per selection */
  const nudgedPinIdRef = useRef<string | null>(null);

  const [isMapReady, setIsMapReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  /* Throttles map error reporting to one per map instance — see the "error" handler below */
  const hasReportedErrorRef = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [isSearchListOpen, setIsSearchListOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isKeyOpen, setIsKeyOpen] = useState(false);
  const [selectedPin, setSelectedPin] = useState<DashboardMapPin | null>(null);

  /* Filter dimension follows the colour dimension: type → filter by type, phase → by phase */
  useEffect(() => {
    setSelectedFilters([]);
  }, [colourBy]);
  const [overlayPoint, setOverlayPoint] = useState<OverlayPoint | null>(null);

  const selectedPinRef = useRef<DashboardMapPin | null>(null);

  const [isExpanded, setIsExpanded] = useState(isExpandedProp);
  /* True while the collapse animation is running — keeps the canvas at the expanded
     size so the shrinking container crops it instead of resizing it mid-animation */
  const [isCollapsing, setIsCollapsing] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const settleCollapse = useCallback(() => {
    clearTimeout(collapseTimerRef.current);
    setIsCollapsing(false);
  }, []);

  useEffect(() => {
    setIsExpanded(isExpandedProp);
  }, [isExpandedProp]);

  useEffect(() => () => clearTimeout(collapseTimerRef.current), []);

  /* The attribute the pins are coloured (and filtered) by — project type or lifecycle phase */
  const colourValueOf = useCallback(
    (pin: DashboardMapPin): string | undefined => (colourBy === "phase" ? pin.phase : pin.category),
    [colourBy]
  );

  const colourValues = useMemo(
    () => Array.from(new Set(pins.map(colourValueOf).filter((value): value is string => !!value))),
    [pins, colourValueOf]
  );

  /* Every colour value gets a colour: explicit mappings first, then auto-assigned from the cycle */
  const effectiveColours = useMemo(() => {
    const provided = colourBy === "phase" ? phaseColours : categoryColours;
    const resolved: { [value: string]: DashboardMapPinColour } = {};
    let cycleIndex = 0;

    colourValues.forEach((value) => {
      const explicit = provided?.[value];
      resolved[value] = explicit ?? PIN_COLOURS[cycleIndex % PIN_COLOURS.length];
      if (!explicit) cycleIndex += 1;
    });

    return resolved;
  }, [colourValues, colourBy, categoryColours, phaseColours]);

  const pinColourFor = useCallback(
    (pin: DashboardMapPin): DashboardMapPinColour => {
      const value = colourValueOf(pin);
      return (value && effectiveColours[value]) || defaultPinColour;
    },
    [colourValueOf, effectiveColours, defaultPinColour]
  );

  /* Key rows for the active colour dimension, ordered by the provided colour map then discovery order */
  const keyEntries = useMemo(() => {
    const provided = colourBy === "phase" ? phaseColours : categoryColours;
    const present = new Set(colourValues);
    const ordered: string[] = [];

    if (provided) {
      Object.keys(provided).forEach((label) => {
        if (present.has(label) && !ordered.includes(label)) ordered.push(label);
      });
    }
    colourValues.forEach((label) => {
      if (!ordered.includes(label)) ordered.push(label);
    });

    return ordered.map((label) => ({ label, colour: effectiveColours[label] }));
  }, [colourValues, effectiveColours, colourBy, categoryColours, phaseColours]);

  /* Filter options are the active dimension's values, in the same order as the key */
  const filterOptions = useMemo(() => keyEntries.map((entry) => entry.label), [keyEntries]);

  const filteredPins = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return pins.filter((pin) => {
      const matchesSearch = !search || pin.name.toLowerCase().includes(search);
      const value = colourValueOf(pin);
      const matchesFilter = !selectedFilters.length || (!!value && selectedFilters.includes(value));

      return matchesSearch && matchesFilter;
    });
  }, [pins, searchText, selectedFilters, colourValueOf]);

  const filteredPinsRef = useRef<DashboardMapPin[]>(filteredPins);
  filteredPinsRef.current = filteredPins;

  /* Projects matching the current search text, for the results list under the input */
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [];
    return filteredPins.slice(0, 6);
  }, [searchText, filteredPins]);

  const toFeatureCollection = useCallback(
    (mapPins: DashboardMapPin[]): FeatureCollection<Point> => ({
      type: "FeatureCollection",
      features: mapPins.map((pin) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [pin.longitude, pin.latitude] },
        properties: { id: pin.id, colour: pinColourFor(pin) },
      })),
    }),
    [pinColourFor]
  );

  const selectPin = useCallback((pin: DashboardMapPin | null) => {
    selectedPinRef.current = pin;
    setSelectedPin(pin);

    const map = mapRef.current;
    if (pin && map) {
      setOverlayPoint(map.project([pin.longitude, pin.latitude]));
    } else {
      setOverlayPoint(null);
    }
  }, []);

  /* Airbnb-style nudge: when a pin is selected, if its card would clip a container edge,
     pan the map by the minimum amount to bring the card fully into view — no hard recenter,
     and only when needed. Runs once per selection; the guard resets when the card closes so
     re-selecting the same pin nudges again. */
  useEffect(() => {
    if (!selectedPin) {
      nudgedPinIdRef.current = null;
      return undefined;
    }
    if (nudgedPinIdRef.current === selectedPin.id) return undefined;
    nudgedPinIdRef.current = selectedPin.id;

    const map = mapRef.current;
    const card = cardRef.current;
    const container = mapContainerRef.current;
    if (!map || !card || !container) return undefined;

    const frame = requestAnimationFrame(() => {
      const cardBox = card.getBoundingClientRect();
      const mapBox = container.getBoundingClientRect();
      const margin = 12;

      const overflowRight = cardBox.right - (mapBox.right - margin);
      const overflowLeft = mapBox.left + margin - cardBox.left;
      const overflowBottom = cardBox.bottom - (mapBox.bottom - margin);
      const overflowTop = mapBox.top + margin - cardBox.top;

      let dx = 0;
      let dy = 0;
      if (overflowRight > 0) dx = overflowRight;
      else if (overflowLeft > 0) dx = -overflowLeft;
      if (overflowBottom > 0) dy = overflowBottom;
      else if (overflowTop > 0) dy = -overflowTop;

      if (dx !== 0 || dy !== 0) map.panBy([dx, dy], { duration: 300 });
    });

    return () => cancelAnimationFrame(frame);
  }, [selectedPin]);

  useEffect(() => {
    if (!mapContainerRef.current) return undefined;

    setHasError(false);
    hasReportedErrorRef.current = false;

    let map: mapboxgl.Map;
    try {
      mapboxgl.accessToken = accessToken || MapboxConstants.ACCESS_TOKEN;
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        // Mercator keeps every pin visible when fitting a global spread (globe hides the far side)
        projection: "mercator",
        center: [-3.5, 55.5],
        zoom: 3,
        attributionControl: false,
      });
    } catch (error) {
      /* Typically an unsupported or unavailable WebGL context. Reported rather than dropped —
         a silently swallowed failure here presents as a plain grey box. */
      logError({ error: error instanceof Error ? error : `DashboardMap failed to construct: ${String(error)}` });
      setHasError(true);
      return undefined;
    }

    mapRef.current = map;

    map.on("load", () => {
      applyBrandMapColours(map);

      PIN_COLOURS.forEach((colour) => {
        const image = createPinImage(colour);
        if (image && !map.hasImage(pinImageId(colour))) {
          map.addImage(pinImageId(colour), image, { pixelRatio: PIN_PIXEL_RATIO });
        }
      });

      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: toFeatureCollection(filteredPinsRef.current),
        cluster: true,
        clusterMaxZoom: 14,
        // Tight radius (~pin footprint) so only genuinely overlapping pins cluster
        clusterRadius: 32,
      });

      map.addLayer({
        id: CLUSTER_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": resolvePinColour("navy"),
          "circle-radius": CLUSTER_RADIUS,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
          "circle-opacity": 0,
          "circle-stroke-opacity": 0,
        },
      });

      map.addLayer({
        id: CLUSTER_COUNT_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
        paint: { "text-color": "#ffffff", "text-opacity": 0 },
      });

      map.addLayer({
        id: PIN_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": ["concat", "dashboard-map-pin-", ["get", "colour"]],
          // 28px layout box scaled to 20px at zoom 0 (0.714x) up to full 28px at zoom 8
          "icon-size": ["interpolate", ["linear"], ["zoom"], 0, 0.714, 8, 1],
          "icon-anchor": "bottom",
          "icon-allow-overlap": true,
        },
        paint: { "icon-opacity": 0 },
      });

      animateMarkersIn(map);
      setIsMapReady(true);
    });

    map.on("click", (event: MapMouseEvent) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: [CLUSTER_LAYER_ID, PIN_LAYER_ID],
      });
      const feature = features[0];

      if (!feature) {
        selectPin(null);
        return;
      }

      if (feature.properties?.cluster) {
        const clusterId = feature.properties.cluster_id as number;
        const source = map.getSource(SOURCE_ID) as GeoJSONSource;

        source.getClusterExpansionZoom(clusterId, (error, zoom) => {
          if (error || zoom === null || zoom === undefined) return;
          map.easeTo({
            center: (feature.geometry as Point).coordinates as [number, number],
            zoom,
          });
        });
        return;
      }

      const pin = filteredPinsRef.current.find((candidate) => candidate.id === feature.properties?.id);
      selectPin(pin ?? null);
    });

    [CLUSTER_LAYER_ID, PIN_LAYER_ID].forEach((layerId) => {
      map.on("mouseenter", layerId, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
      });
    });

    map.on("move", () => {
      const pin = selectedPinRef.current;
      if (pin) {
        setOverlayPoint(map.project([pin.longitude, pin.latitude]));
      }
    });

    map.on("error", (event) => {
      // Only treat unrecoverable auth/style failures as fatal, not transient tile errors
      const status = (event.error as { status?: number })?.status;
      if (status === 401 || status === 403) {
        setHasError(true);
        logError({ error: event.error ?? `DashboardMap authorisation failed with status ${status}` });
        return;
      }
      if (hasReportedErrorRef.current) return;
      hasReportedErrorRef.current = true;
      logError({ error: event.error ?? "DashboardMap emitted an error with no error object" });
    });

    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
    // The map instance is created once; data and viewport updates are handled by the effects below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    source?.setData(toFeatureCollection(filteredPins));

    /* Keep the open card in sync with refreshed data: re-point to the fresh pin object,
       or clear the selection if the pin is no longer present */
    const { current } = selectedPinRef;
    if (current) {
      const fresh = filteredPins.find((pin) => pin.id === current.id);
      if (!fresh) {
        selectPin(null);
      } else if (fresh !== current) {
        selectPin(fresh);
      }
    }

    if (!filteredPins.length) return;

    const bounds = filteredPins.reduce(
      (acc, pin) => acc.extend([pin.longitude, pin.latitude]),
      new LngLatBounds(
        [filteredPins[0].longitude, filteredPins[0].latitude],
        [filteredPins[0].longitude, filteredPins[0].latitude]
      )
    );

    map.fitBounds(bounds, {
      padding: 64,
      maxZoom: filteredPins.length === 1 ? 5 : 10,
      duration: 700,
    });
  }, [filteredPins, isMapReady, toFeatureCollection, selectPin]);

  const onSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setIsSearchListOpen(value.trim().length > 0);
  }, []);

  const onClearSearch = useCallback(() => {
    setSearchText("");
    setIsSearchListOpen(false);
  }, []);

  const onSelectSearchResult = useCallback(
    (pin: DashboardMapPin) => {
      setIsSearchListOpen(false);
      const map = mapRef.current;
      if (map) {
        map.flyTo({ center: [pin.longitude, pin.latitude], zoom: Math.max(map.getZoom(), 8), duration: 700 });
      }
      /* flyTo already centres the pin, so skip the edge-nudge for this selection */
      nudgedPinIdRef.current = pin.id;
      selectPin(pin);
    },
    [selectPin]
  );

  const onToggleFilterOpen = useCallback(() => setIsFilterOpen((isOpen) => !isOpen), []);

  const onToggleKeyOpen = useCallback(() => setIsKeyOpen((isOpen) => !isOpen), []);

  /* Close the filter / key popovers and search results when clicking outside them */
  useEffect(() => {
    if (!isFilterOpen && !isSearchListOpen && !isKeyOpen) return undefined;

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (filterRef.current && !filterRef.current.contains(target)) {
        setIsFilterOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(target)) {
        setIsSearchListOpen(false);
      }
      if (keyRef.current && !keyRef.current.contains(target)) {
        setIsKeyOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isFilterOpen, isSearchListOpen, isKeyOpen]);

  const onToggleFilter = useCallback((value: string) => {
    setSelectedFilters((current) =>
      current.includes(value) ? current.filter((c) => c !== value) : [...current, value]
    );
  }, []);

  const onClearFilters = useCallback(() => setSelectedFilters([]), []);

  const onToggleExpand = useCallback(() => {
    setIsExpanded((current) => {
      if (current) {
        /* Collapsing: hold the canvas at the expanded size until the animation settles */
        setIsCollapsing(true);
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = setTimeout(settleCollapse, COLLAPSE_SETTLE_MILLISECONDS);
      } else {
        setIsCollapsing(false);
      }
      onExpandToggle?.(!current);
      return !current;
    });
  }, [onExpandToggle, settleCollapse]);

  const onContainerTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && event.propertyName === "height") {
        settleCollapse();
      }
    },
    [settleCollapse]
  );

  const onZoomIn = useCallback(() => mapRef.current?.zoomIn(), []);

  const onZoomOut = useCallback(() => mapRef.current?.zoomOut(), []);

  return {
    mapContainerRef,
    hasError,
    filterOptions,
    selectedFilters,
    filterDimension: colourBy,
    isFilterOpen,
    filterRef,
    keyRef,
    cardRef,
    keyEntries,
    isKeyOpen,
    selectedPin,
    overlayPoint,
    isEmpty: !filteredPins.length,
    isExpanded,
    containerHeight: isExpanded ? expandedHeight : height,
    canvasHeight: isExpanded || isCollapsing ? expandedHeight : height,
    searchText,
    searchRef,
    searchResults,
    isSearchListOpen,
    pinColourFor,
    onSearchChange,
    onClearSearch,
    onSelectSearchResult,
    onToggleFilterOpen,
    onToggleFilter,
    onClearFilters,
    onToggleKeyOpen,
    onToggleExpand,
    onContainerTransitionEnd,
    onZoomIn,
    onZoomOut,
  };
};
